import moment from 'utils/cops-moment';
import { SI_POSTFIXES } from 'utils/i18n';
import {
  announcementDateFormats,
  leftParenthesisAlternative,
  referenceTypes,
  rightParenthesisAlternative,
  searchOperators,
  slashAlternative,
  sortTypes,
  urlSigns,
} from './app-configs';

/* eslint-disable */
export const easeInOut = (currentTime, start, change, duration) => {
  currentTime /= duration / 2;
  if (currentTime < 1) {
    return (change / 2) * currentTime * currentTime + start;
  }
  currentTime -= 1;
  return (-change / 2) * (currentTime * (currentTime - 2) - 1) + start;
};

export const checkEnv = () => {
  if (window.location.origin.includes('dev')) {
    return 'DEV';
  } else if (window.location.origin.includes('qas')) {
    return 'QAS';
  }
  if (window.location.origin.includes('localhost')) {
    return 'LCL';
  }
  return 'PRD';
};

export const animateScrollTo = (el, offset = 0, duration = 500) => {
  const targetOffset = el.getBoundingClientRect().top + window.pageYOffset - offset;
  const start = window.pageYOffset;
  const change = targetOffset - start;
  const startTime = performance.now();

  const easeInOutQuad = (t, b, c, d) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const position = easeInOutQuad(elapsed, start, change, duration);
    window.scrollTo(0, position);

    if (elapsed < duration) {
      requestAnimationFrame(animate);
    } else {
      window.scrollTo(0, targetOffset); // snap to final position
    }
  };

  requestAnimationFrame(animate);
};
/* eslint-enable */

export const decodeUrlParams = obj =>
  Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    try {
      Object.assign(acc, { [key]: decodeURIComponent(value) });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      Object.assign(acc, { [key]: value });
    }
    return acc;
  }, {});

export const camelCaseToFriendlyText = text =>
  text && text.replace(/([A-Z])/g, ' $1').replace(/^./, str => str?.toUpperCase());

export const toSnakeCase = text =>
  text && text.replace(
    /\.?([a-z][A-Z]|[a-zA-Z]\s[a-zA-Z]|[a-zA-Z]-[a-zA-Z])/g,
    (x) => {
      if (x[1] === '-' || x[1] === ' ') {
        return `${x[0]?.toLowerCase()}-${x[2]?.toLowerCase()}`;
      }
      return `${x[0]?.toLowerCase()}-${x[1]?.toLowerCase()}`;
    },
  );

export const toPascalCaseWithSpaces = (text) => {
  const splitnames = text.split(' ');
  splitnames.forEach((n) => {
    if (
      n
        ?.toString()
        ?.charAt(0)
        ?.match(/[A-Za-z]/i)
    ) {
      splitnames[splitnames.indexOf(n)] =
        n
          ?.toString()
          ?.charAt(0)
          ?.toUpperCase() +
        n
          ?.toString()
          ?.substr(1)
          ?.toLowerCase();
    }
  });
  return splitnames.join(' ');
};

export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);

export const sanitize = text => text.replace(/<\/?[^>]+(>|$)/g, '');

export const getUrlParameters = ({ params, equalsSign = '=', andSign = '&' }) =>
  params
    ? Object.keys(params)
      ?.filter(key => params[key])
      ?.map(key => `${key}${equalsSign}${params[key]}`)
      .join(andSign)
    : '';

export const transformSAPId = ({ originalId, isMaterialNumber = false }) => {
  // for material numbers, only show last 10 chars
  if (isMaterialNumber) {
    return originalId.toString().slice(-10);
  }

  // Only proceed if the originalId is a number
  if (!isNaN(parseFloat(originalId)) && isFinite(originalId)) {
    return originalId.toString().replace(/\b0+/g, '');
  }
  // If not applicable, return the non-parsed orginal id
  return originalId;
};

export const getValueAndPostfix = (number) => {
  // eslint-disable-next-line no-bitwise
  const tier = (Math.log10(Math.abs(number)) / 3) | 0;
  const postFixes = SI_POSTFIXES;
  if (tier === 0) {
    return { value: number, postfix: postFixes[0] };
  }
  const postfix = postFixes[tier];
  const scale = 10 ** (tier * 3);
  const scaled = number / scale;
  let value = `${scaled && scaled.toFixed(1)}`;
  if (/\.0$/.test(value)) {
    value = value.substr(0, value.length - 2);
  }
  return { value, postfix };
};

export const transformFiltersToUrlParams = (activeFilters) => {
  const filters = Object.keys(activeFilters).reduce((all, key) => {
    let filterString = '';
    // If there are no active filters, don't include it in the filter string.
    if (activeFilters[key].length === 0) {
      return all;
    }

    if (activeFilters[key].length === 1) {
      // eslint-disable-next-line prefer-destructuring
      filterString = activeFilters[key][0];
    } else {
      // eslint-disable-next-line
      filterString = `${activeFilters[key].join(urlSigns.valueSeparator)}`;
    }

    return { ...all, [key]: filterString?.replace('/', slashAlternative) };
  }, {});

  return getUrlParameters({
    params: filters,
    equalsSign: urlSigns.keyValueSeparator,
    andSign: urlSigns.filterSeparator,
  });
};

export const transformUrlParamsToFilters = (urlParams) => {
  if (urlParams) {
    return urlParams.split(urlSigns.filterSeparator).reduce((all, filter) => {
      const filterParts = filter.split(urlSigns.keyValueSeparator);
      if (filterParts.length === 2 && filterParts[1] !== '') {
        const [filterName, filterRawValues] = filterParts;

        let filterValues = filterRawValues;
        if (filterName !== 'geo') {
          filterValues = filterRawValues
            .split(urlSigns.valueSeparator);
        } else {
          filterValues = [filterRawValues];
        }

        return { ...all, [filterName]: filterValues };
      }
      return all;
    }, {});
  }
  return {};
};

export const transformUrlParamsToSearchFilters = (urlParams) => {
  if (urlParams) {
    return urlParams.split(urlSigns.filterSeparator).reduce((all, filter) => {
      const filterParts = filter.split(urlSigns.keyValueSeparator);
      if (filterParts.length === 2 && filterParts[1] !== '') {
        const [filterName, filterRawValues] = filterParts;
        return { ...all, [filterName]: filterRawValues };
      }
      return all;
    }, {});
  }
  return null;
};

export const firstLetterToLowercase = str => str.charAt(0)?.toLowerCase() + str.slice(1);
export const sortCOPSModelToParam = (sortModel = []) => {
  const sort = sortModel?.map(model =>
    // eslint-disable-next-line max-len
    `${firstLetterToLowercase(model.sortColumn)}${urlSigns.keyValueSeparator}${model.sortDirection}${urlSigns.sortIndexSeparator}${model.sortIndex}`,
  ).join(urlSigns.filterSeparator);
  return sort;
};

export const sortParamToCOPSModel = (sortParam) => {
  if (!sortParam) {
    return [];
  }
  const sortArray = sortParam.split(urlSigns.filterSeparator);
  const sortModel = sortArray?.map((sort) => {
    const [keyValue, index] = sort.split(urlSigns.sortIndexSeparator);
    const [key, value] = keyValue.split(urlSigns.keyValueSeparator);
    return {
      sortIndex: Number(index),
      sortColumn: firstLetterToLowercase(key),
      sortDirection: value,
    };
  });
  return sortModel;
};

export const sortParamToAGModel = (sortParam) => {
  if (!sortParam) {
    return [];
  }
  const sortArray = sortParam.split(urlSigns.filterSeparator);
  const sortModel = sortArray?.map((sort) => {
    const [keyValue, index] = sort.split(urlSigns.sortIndexSeparator);
    const [key, value] = keyValue.split(urlSigns.keyValueSeparator);
    return {
      sortIndex: Number(index),
      colId: firstLetterToLowercase(key),
      sort: value === sortTypes.ascending ? sortTypes.ascending : sortTypes.descending,
    };
  });
  return sortModel;
};

export const addDataToNoneView = (
  worklistId,
  views,
  defaultFilters = '',
  defaultSort = '',
) => views?.map((v) => {
  if (v?.isNone) {
    let result = {
      id: `${worklistId}_none`,
      ...v,
    };
    if (defaultFilters) {
      result = {
        ...result,
        filterData: defaultFilters,
      };
    }
    if (defaultSort) {
      result = {
        ...result,
        sorting: sortParamToCOPSModel(defaultSort),
      };
    }
    return result;
  }
  return v;
});

export const getObjectTypeById = (id) => {
  if (id?.includes('customer')) {
    return referenceTypes.customer;
  }
  if (id?.includes('installedat')) {
    return referenceTypes.installedAt;
  }
  if (id?.includes('equipment')) {
    return referenceTypes.equipment;
  }
  if (id?.includes('order')) {
    return referenceTypes.order;
  }
  if (id?.includes('notification')) {
    return referenceTypes.notification;
  }
  return null;
};

export const sanitizeNote = note => note
  .replace(/<div><br><\/div>/gi, '<br>')
  .replace(/<div>/gi, '<br>')
  .replace(/<\/div>/gi, '');

export const generatePreviewId = block => `${block.laneId}||${block.linkedEntityId || ''}`;

export const sanitizeName = name => name
  .replace(/\s*/g, '')
  .replace(/_/g, '')
  .replace(/-/g, '');

export const determineOperatorAndValue = (value) => {
  if (value.match(/^\(.*\)$/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/^\((.*)\)$/)[1],
      operator: searchOperators.contains.id,
    };
  } else if (value.match(/^\*.*\*$/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/\*(.*)\*/)[1],
      operator: searchOperators.matchAny.id,
    };
  } else if (value.match(/.*\*$/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/(.*)\*/)[1],
      operator: searchOperators.startsWith.id,
    };
  } else if (value.match(/^\*.*/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/\*(.*)/)[1],
      operator: searchOperators.endsWith.id,
    };
  } else if (value.match(/“.*”/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/“(.*)”/)[1],
      operator: searchOperators.exact.id,
    };
  } else if (value.match(/^\[.*\]$/)) {
    return {
      // eslint-disable-next-line prefer-destructuring
      value: value.match(/\[(.*)\]/)[1],
      operator: searchOperators.matchAll.id,
    };
  }
  return {
    value,
    operator: null,
  };
};

export const queryStringtoSearchFilters = (queryString) => {
  if (queryString) {
    const filters = queryString.split(urlSigns.filterSeparator);
    return filters?.map((f) => {
      const [id, tag] = f.split(urlSigns.keyValueSeparator);
      const [prefix, operatorValue] = tag?.split(':');
      const { value, operator } = determineOperatorAndValue(operatorValue);
      return {
        id,
        prefix,
        value: decodeURIComponent(value),
        operator,
      };
    });
  }
  return [];
};

export const convertToKpiObject = targetKpiKey => ({
  value: targetKpiKey?.value,
  labelTranslationKey: targetKpiKey?.description,
  uri: targetKpiKey?.url,
  clickThrough: !!targetKpiKey?.url,
});

export const encodeContainsOperator = v => v
  .replace('(', leftParenthesisAlternative)
  .replace(')', rightParenthesisAlternative);

export const decodeContainsOperator = v => v
  .replace(leftParenthesisAlternative, '(')
  .replace(rightParenthesisAlternative, ')');

export const getAmountFilters = (
  activeFilters = {},
  filters,
  isGroup = false,
  fixedFilterValues = {},
) => {
  let amount = 0;
  const relevantFilters = Object.entries(activeFilters)?.map((filter) => {
    const [key, value] = filter;
    if (fixedFilterValues[key]) {
      return [key, value?.filter(v => !fixedFilterValues[key]?.find(k => k.includes(v)))];
    }
    return filter;
  });
  for (const [filterType, filterValue] of relevantFilters) {
    if (!isGroup || filters?.find(e => e.id === filterType)) {
      amount += filterValue.length;
      const filter = filters?.find(f => f.id === filterType);
      const isRange = filter?.type === 'combinedRange' || filter?.type === 'range';

      if (
        isRange && filterValue.length !== 0
      ) {
        amount -= 1;
      }
      if (
        filterType === 'userStatus' &&
        filterValue?.length
      ) {
        amount -= 1;
        amount += filterValue[0].split(';')?.length;
      }
    }
  }
  return amount >= 0 ? amount : 0;
};

export const isObjectNullOrEmpty = object => !object || Object.keys(object).length === 0;

export const getFullDateFromFormValues = (values) => {
  if (values?.date && values?.hour && values?.minute) {
    const dateString = [
      values.date.format(announcementDateFormats.date),
      values.hour.format(announcementDateFormats.hour),
      values.minute.format(announcementDateFormats.minute),
    ].join(' ');
    return moment(dateString, announcementDateFormats.fullDate);
  }
  return null;
};

export const determineSortIcon = (id, type, ascending) => {
  let iconType = ascending ?
    'fa-regular fa-arrow-down-wide-short' :
    'fa-regular fa-arrow-down-short-wide';
  if (type === 'sapId' || type === 'number') {
    iconType = ascending ? 'fa-light fa-arrow-down-1-9' : 'fa-light fa-arrow-down-9-1';
  }
  if (type === 'string' || id === 'Equipment') {
    iconType = ascending ? 'fa-light fa-arrow-down-a-z' : 'fa-light fa-arrow-down-z-a';
  }
  return `<i class="${iconType}"></i>`;
};

export const checkErrorObject = obj => Object.values(obj).flatMap(value =>
  // eslint-disable-next-line
  value === undefined
    ? []
    : Array.isArray(value)
      ? value
      : checkErrorObject(value),
);

export const getDuration = (duration, unit = null) => {
  if (duration?.match(/^\d+\./g)) {
    // eslint-disable-next-line
    const durationObj = moment.duration(duration, unit);
    const days = Math.floor(durationObj.asDays());
    const hours = durationObj.hours();
    const totalHours = Math.floor(durationObj.asHours());
    return `${totalHours}h (${days}d ${hours}h)`;
  }
  return moment(duration, 'HH:mm:ss')
    .format('H[h]mm')
    .replace(/00$/, '');
};

export const customDurationFormat = (duration, unit) => {
  let days = 0;
  let hours = 0;
  if (unit === 'hours') {
    days = Math.floor(duration / 24);
    hours = duration - (days * 24);
  } else if (unit === 'days') {
    days = duration;
  } else {
    hours = duration;
  }

  const formattedDuration = `${days}.${moment.duration(hours, 'hours').format('HH:mm:ss')}`;

  return formattedDuration;
};

export const checkTags = (tags) => {
  if (tags.length > 0) {
    const tagWeights = [
      { tag: 'customer', weight: 1 },
      { tag: 'installedat', weight: 2 },
      { tag: 'equipment', weight: 3 },
      { tag: 'order', weight: 5 },
      { tag: 'technician', weight: 6 },
    ];
    const weightedTags = tags?.map((t) => {
      const foundWeight = tagWeights?.find(w => w.tag === t.type);
      if (foundWeight) {
        return {
          ...t,
          weight: foundWeight.weight,
        };
      }
      // If no weighted tag is found, default it to below.
      return {
        ...t,
        weight: 100,
      };
    });

    return weightedTags?.sort((a, b) => a.weight - b.weight) || [];
  }
  return [];
};
